import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import Menu from '../../components/Menu/Menu';
import Ticker from './Ticker/Ticker';
import Book from './Book/Book';
import useWebSocket from 'react-use-websocket';
import Wallet from '../../components/Wallet/Wallet';
import NewOrderButton from '../../components/NewOrder/NewOrderButton';
import NewOrderModal from '../../components/NewOrder/NewOrderModal';
import CandleChart from './CandleChart';
import MyHoldings from './MyHoldings';
import SelectSymbol from '../../components/SelectSymbol/SelectSymbol';
import Footer from '../../components/Footer/Footer';
import Toast from '../../components/Toast/Toast';
import AIInsights from './AIInsights';
import SentimentWidget from './SentimentWidget';
import StatCard from '../../components/StatCard/StatCard';
import SetupChecklist from '../../components/Onboarding/SetupChecklist';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import { getActiveTargets, sellNow, getFullBalance, stopAllBots } from '../../services/ExchangeService';
import { useIntl } from 'react-intl';
import logger from '../../utils/logger';
import { fmtMoney, fmtPrice, fmtQty, fmtPctSigned } from '../../utils/format';

/**
 * Active-bots card for the Dashboard. Runs its own ~5s poll of getActiveTargets()
 * and lets the user force-close a position (with a styled ConfirmModal). One row
 * per position with aligned columns.
 *
 * props:
 * - onNotify(notification)
 * - onTargets(rows)     : lifts the rows up so the Dashboard can compute KPIs.
 * - onSelectSymbol(sym) : clicking a position drives the candle chart below.
 * - activeSymbol        : currently charted symbol (row highlight).
 */
function BotPositionsCard({ onNotify, onTargets, onSelectSymbol, activeSymbol }) {
  const { formatMessage } = useIntl();
  const t = (id) => formatMessage({ id });
  const [rows, setRows] = useState([]);
  const [confirm, setConfirm] = useState(null); // { tg }

  const load = useCallback(() => {
    getActiveTargets()
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setRows(list);
        if (onTargets) onTargets(list);
      })
      .catch(err => logger.error(err.response ? err.response.data : err.message));
  }, [onTargets]);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  function doSellNow(tg) {
    setConfirm(null);
    sellNow(tg.engine, tg.sessionId)
      .then(() => { onNotify({ type: 'success', text: t('positions.sold') }); load(); })
      .catch(err => {
        logger.error(err.response ? err.response.data : err.message);
        onNotify({ type: 'error', text: err.response ? err.response.data : err.message });
      });
  }

  function pnlFor(tg) {
    const pnl = parseFloat(tg.unrealizedPnl);
    const pnlPct = parseFloat(tg.unrealizedPnlPct);
    const positive = isNaN(pnlPct) ? (isNaN(pnl) ? true : pnl >= 0) : pnlPct >= 0;
    const text = `${!isNaN(pnl) ? (pnl >= 0 ? '+' : '') + fmtMoney(pnl) : '—'}${!isNaN(pnlPct) ? ` (${fmtPctSigned(pnlPct)})` : ''}`;
    return { positive, text };
  }

  return (
    <div className="col-12 mb-4">
      <div className="card border-0 shadow">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h2 className="fs-5 fw-bold mb-0">{t('dashboard.botPositions')}</h2>
        </div>
        <div className="table-responsive">
          <table className="table table-hover gh-table-stack align-items-center mb-0">
            <thead>
              <tr>
                <th>{t('positions.symbol')}</th>
                <th>{t('positions.bot')}</th>
                <th className="num">{t('positions.qty')} @ {t('positions.avgBuy')}</th>
                <th className="num">{t('positions.current')}</th>
                <th className="num">{t('positions.pnl')}</th>
                <th className="num" title={t('positions.feesHint')}>{t('positions.fees')}</th>
                <th className="num">{t('positions.targetSell')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length
                ? rows.map(tg => {
                  const { positive, text } = pnlFor(tg);
                  // Same clickable-row pattern as MyHoldings/Positions: the row
                  // (symbol included) drives the candle chart below.
                  const onActivate = () => onSelectSymbol && onSelectSymbol(tg.symbol);
                  return (
                    <tr
                      key={`dbot-${tg.engine}-${tg.sessionId}`}
                      className={`gh-row-click${activeSymbol === tg.symbol ? ' table-active' : ''}`}
                      role="button"
                      tabIndex={0}
                      aria-label={tg.symbol}
                      onClick={onActivate}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onActivate(); } }}>
                      <td data-label={t('positions.symbol')} className="fw-bold">{tg.symbol}</td>
                      <td data-label={t('positions.bot')}>
                        <span className={`gh-badge ${tg.engine === 'TODAY' ? 'gh-badge-info' : 'gh-badge-gold'}`}>
                          {tg.engine === 'TODAY' ? 'Today' : 'Risk'}
                        </span>
                      </td>
                      <td data-label={`${t('positions.qty')} @ ${t('positions.avgBuy')}`} className="num">{fmtQty(tg.qty)} @ {fmtPrice(tg.avgEntry)}</td>
                      <td data-label={t('positions.current')} className="num fw-bold">{tg.currentPrice != null ? fmtPrice(tg.currentPrice) : '—'}</td>
                      <td data-label={t('positions.pnl')} className={`num fw-bold ${positive ? 'gh-pos' : 'gh-neg'}`}>{text}</td>
                      {/* Fee transparency: entry fee already paid + exit fee if sold now. */}
                      <td data-label={t('positions.fees')} className="num text-muted" title={t('positions.feesHint')}>
                        {tg.entryFeePaid != null
                          ? `${fmtMoney(tg.entryFeePaid)} + ${tg.exitFeeNow != null ? fmtMoney(tg.exitFeeNow) : '—'}`
                          : '—'}
                      </td>
                      <td data-label={t('positions.targetSell')} className="num">{fmtPrice(tg.targetSell)}</td>
                      <td data-label={t('common.actions')}>
                        <button type="button" className="btn btn-sm btn-danger py-0 px-2" onClick={(e) => { e.stopPropagation(); setConfirm({ tg }); }}>
                          {t('positions.sellNow')}
                        </button>
                      </td>
                    </tr>
                  );
                })
                : (<tr><td colSpan={8} className="text-muted">{t('positions.noTargets')}</td></tr>)
              }
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmModal
        open={!!confirm}
        title={t('positions.sellNowConfirm')}
        message={confirm ? confirm.tg.symbol : ''}
        details={confirm ? [
          { label: t('positions.qty'), value: fmtQty(confirm.tg.qty) },
          { label: t('positions.current'), value: fmtPrice(confirm.tg.currentPrice) }
        ] : []}
        pnl={confirm ? pnlFor(confirm.tg) : null}
        confirmLabel={t('positions.sellNow')}
        onConfirm={() => doSellNow(confirm.tg)}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

function Dashboard() {

  const { formatMessage } = useIntl();
  const t = (id) => formatMessage({ id });

  const history = useHistory();

  const [tickerState, setTickerState] = useState({});
  const [balanceState, setBalanceState] = useState({});
  const [bookState, setBookState] = useState({});
  const [wallet, setWallet] = useState({});
  // The charted symbol is PERSISTED (not consumed) so the user's choice sticks
  // across re-renders/remounts/reloads instead of snapping back to BTCUSDC.
  // A "Today" top-pair click writes this same key before navigating here.
  const [chartSymbol, setChartSymbol] = useState(() => {
    try {
      const saved = localStorage.getItem('goldhub_chart_symbol');
      if (saved) return saved;
    } catch (e) { /* ignore storage errors */ }
    return 'BTCUSDC';
  });

  // KPI strip state — portfolio value (USDC) + lifted bot targets for P&L / counts.
  const [portfolioValue, setPortfolioValue] = useState(null);
  const [botTargets, setBotTargets] = useState([]);
  const [stopAllOpen, setStopAllOpen] = useState(false);

  function selectChart(symbol) {
    if (!symbol || symbol === chartSymbol) return;
    try { localStorage.setItem('goldhub_chart_symbol', symbol); } catch (e) { /* ignore */ }
    setChartSymbol(symbol);
  }
  const [notification, setNotification] = useState({ type: '', text: '' });
  const [sentimentData, setSentimentData] = useState(null);

  // Portfolio value (total USDC estimate) for the hero KPI.
  useEffect(() => {
    getFullBalance('USDC', false)
      .then((info) => {
        const raw = info && info.fiatEstimate;
        const n = parseFloat(String(raw).replace(/[^0-9.\-]/g, ''));
        setPortfolioValue(Number.isFinite(n) ? n : null);
      })
      .catch(err => logger.error(err.response ? err.response.data : err.message));
  }, []);

  // WebSocket ticks can arrive several times per second. Applying a separate
  // setState per message re-renders the whole Dashboard subtree on every tick
  // (render storm). Instead we COALESCE: incoming messages are stashed in a
  // pending buffer (ref, no render) and flushed at most once per animation
  // frame, throttled to ~250ms. One flush applies all four state slices in a
  // single React batch, preserving the exact same data semantics — just fewer
  // renders. The book accumulation keeps merging into the latest committed
  // state so no updates are lost between flushes.
  const FLUSH_INTERVAL_MS = 250;
  const pendingRef = useRef({ ticker: null, balance: null, book: null, sentiment: null });
  const rafRef = useRef(null);
  const lastFlushRef = useRef(0);

  const flushPending = useCallback(() => {
    rafRef.current = null;
    lastFlushRef.current = Date.now();
    const pending = pendingRef.current;
    pendingRef.current = { ticker: null, balance: null, book: null, sentiment: null };

    if (pending.ticker) setTickerState(pending.ticker);
    if (pending.balance) setBalanceState(pending.balance);
    if (pending.book && pending.book.length) {
      // New object reference so consumers (Book follows chartSymbol) re-render.
      setBookState(prev => {
        const next = { ...prev };
        pending.book.forEach(b => { next[b.symbol] = b; });
        return next;
      });
    }
    if (pending.sentiment) setSentimentData(pending.sentiment);
  }, []);

  const scheduleFlush = useCallback(() => {
    if (rafRef.current !== null) return;
    const elapsed = Date.now() - lastFlushRef.current;
    if (elapsed >= FLUSH_INTERVAL_MS) {
      rafRef.current = requestAnimationFrame(flushPending);
    } else {
      rafRef.current = setTimeout(() => {
        rafRef.current = requestAnimationFrame(flushPending);
      }, FLUSH_INTERVAL_MS - elapsed);
    }
  }, [flushPending]);

  // Flush any buffered updates on unmount so nothing is left pending.
  useEffect(() => () => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      clearTimeout(rafRef.current);
    }
  }, []);

  const { lastJsonMessage } = useWebSocket(process.env.REACT_APP_WS_URL, {
    onOpen: () => {
      logger.log(`Connected to App WS`);
    },
    onMessage: () => {
      if (lastJsonMessage) {
        if (lastJsonMessage.ticker) pendingRef.current.ticker = lastJsonMessage.ticker;
        else if (lastJsonMessage.balance) {
          pendingRef.current.balance = lastJsonMessage.balance;
        }
        else if (lastJsonMessage.book) {
          // Accumulate book entries across buffered messages so none are dropped.
          if (!pendingRef.current.book) pendingRef.current.book = [];
          pendingRef.current.book.push(...lastJsonMessage.book);
        }
        else if (lastJsonMessage.sentiment) {
          pendingRef.current.sentiment = lastJsonMessage.sentiment;
        }
        scheduleFlush();
      }
    },
    queryParams: { 'token': localStorage.getItem("token") },
    onError: (event) => {
      // Transient WebSocket error — log only, don't show the raw event to the user.
      logger.error(event);
    },
    shouldReconnect: (closeEvent) => true,
    reconnectInterval: 3000
  });

  function onWalletUpdate(walletObj) {
    setWallet(walletObj);
  }

  function onSubmitOrder(order) {
    history.push('/orders/' + order.symbol);
  }

  function onChangeSymbol(event) {
    selectChart(event.target.value);
  }

  // Single source of truth for the charted symbol. Wired down to the
  // Holdings list, Market-24h ticker and the order-book header so any of
  // them can drive the top chart.
  function onSelectSymbol(symbol) {
    selectChart(symbol);
  }

  const onBotTargets = useCallback((rows) => setBotTargets(rows), []);

  function doStopAll() {
    setStopAllOpen(false);
    stopAllBots()
      .then(() => {
        setNotification({ type: 'success', text: t('dashboard.stopAllDone') });
        setTimeout(() => window.location.reload(), 800);
      })
      .catch(err => {
        logger.error(err.response ? err.response.data : err.message);
        setNotification({ type: 'error', text: err.response ? err.response.data : err.message });
      });
  }

  // --- KPI derivations from the lifted bot targets ---
  const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
  const todayPnl = botTargets.reduce((a, tg) => a + num(tg.unrealizedPnl), 0);
  const todayPnlPositive = todayPnl >= 0;
  const activeBots = botTargets.length;
  // Nearest target: smallest positive "to-go %" across open positions.
  const nearest = botTargets
    .map(tg => ({ symbol: tg.symbol, pct: parseFloat(tg.pctToGo) }))
    .filter(x => Number.isFinite(x.pct))
    .sort((a, b) => a.pct - b.pct)[0];

  return (
    <React.Fragment>
      <Menu />
      <main className="content">
        <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
          <div className="d-block mb-3 mb-md-0">
            <h1 className="h4 mb-0">{t('dashboard.title')}</h1>
          </div>
          <div className="btn-toolbar mb-md-0 align-items-center">
            <button
              type="button"
              className="btn btn-danger btn-sm gh-killswitch me-2"
              onClick={() => setStopAllOpen(true)}
              aria-label={t('dashboard.stopAll')}>
              {t('dashboard.stopAll')}
            </button>
            <div className="d-inline-flex align-items-center">
              <SelectSymbol onChange={onChangeSymbol} symbol={chartSymbol} />
            </div>
            <div className="ms-2 ms-lg-3">
              <NewOrderButton />
            </div>
          </div>
        </div>

        {/* First-run onboarding checklist (dismissible, hidden once complete) */}
        <SetupChecklist />

        {/* KPI strip — row 0 */}
        <div className="gh-grid mb-4">
          <div>
            <StatCard
              label={t('dashboard.portfolioValue')}
              gold
              value={<span className="num">{portfolioValue != null ? fmtMoney(portfolioValue) : '—'} <span className="fs-6 text-muted">USDC</span></span>}
            />
          </div>
          <div>
            <StatCard
              label={t('dashboard.todayPnl')}
              value={<span className={`num ${todayPnlPositive ? 'gh-pos' : 'gh-neg'}`}>{todayPnlPositive ? '+' : ''}{fmtMoney(todayPnl)} <span className="fs-6 text-muted">USDC</span></span>}
              sub={t('dashboard.todayPnlSub')}
            />
          </div>
          <div>
            <StatCard
              label={t('dashboard.activeBots')}
              value={<span className="num">{activeBots}</span>}
              sub={t('dashboard.activeBotsSub')}
            />
          </div>
          <div>
            <StatCard
              label={t('dashboard.nearestTarget')}
              value={nearest
                ? <span className="num">{nearest.symbol}</span>
                : <span className="text-muted">—</span>}
              sub={nearest && Number.isFinite(nearest.pct)
                ? <span className="num">{nearest.pct > 0 ? '+' : ''}{nearest.pct.toFixed(1)}% {t('positions.toGo').toLowerCase()}</span>
                : t('dashboard.noTargetsShort')}
            />
          </div>
        </div>

        {/* Active bots promoted above the chart */}
        <div className="row">
          <BotPositionsCard onNotify={setNotification} onTargets={onBotTargets} onSelectSymbol={onSelectSymbol} activeSymbol={chartSymbol} />
        </div>

        {/* Chart (8 cols) beside the order book (4 cols) */}
        <div className="row">
          <div className="col-12 col-lg-8 mb-4">
            <CandleChart symbol={chartSymbol} />
          </div>
          <Book data={bookState} symbol={chartSymbol} cols="col-12 col-lg-4 mb-4" />
        </div>

        <div className="row">
          <MyHoldings
            onSelectSymbol={onSelectSymbol}
            activeSymbol={chartSymbol}
            tickerData={tickerState} />
        </div>
        <div className="row">
          <div className="col-12">
            <Ticker
              data={tickerState}
              activeSymbol={chartSymbol}
              onSelectSymbol={onSelectSymbol} />
          </div>
        </div>
        <div className="row">
          <Wallet data={balanceState} onUpdate={onWalletUpdate} />
        </div>
        <div className="row">
          <AIInsights />
          <SentimentWidget wsData={sentimentData} />
        </div>
        <Footer />
      </main>
      <NewOrderModal wallet={wallet} onSubmit={onSubmitOrder} />

      <ConfirmModal
        open={stopAllOpen}
        title={t('dashboard.stopAll')}
        message={t('dashboard.stopAllConfirm')}
        confirmLabel={t('dashboard.stopAll')}
        onConfirm={doStopAll}
        onCancel={() => setStopAllOpen(false)}
      />

      <Toast type={notification.type} text={notification.text} />
    </React.Fragment>
  );
}

export default Dashboard;
